import { AlertDefaults, AlertType, PriorityType, SingleAlertDefaultsRequest } from "src/app/hickory-api";
import { AlertConfig } from "../alert-config";
import { AlertModelService } from "../alert-model.service";
import { AlertChannelsHelper, BasicChannel } from "./alert-channels-helper";
import { AlertFields, AlertFieldsHelper } from "./alert-fields-helper";
import { AlertMessageHelper } from "./alert-message-helper";
import { AlertPollHelper } from "./alert-poll-helper";
import { AlertPriorityHelper } from "./alert-priority-helper";
import { AlertRecipientsHelper } from "./alert-recipients-helper";
import { AlertSurveyHelper } from "./alert-survey-helper";
import { isValidIntegerId } from "src/app/shared/model/utility";

export class AlertDefaultsHelper {

  static initDefaults(alert?: AlertDefaults, alertType?: AlertType, config?: AlertConfig): AlertDefaults {
    if (!alert) alert = {};
    if (!alert.alertType) alert.alertType = alertType || config?.alertType || AlertType.Default;
    if (!alert.fields) alert.fields = [];
    if (!alert.channels) alert.channels = {};
    if (!alert.message) alert.message = {};
    AlertMessageHelper.initMessage(alert.message);
    if (alert.alertType === AlertType.Poll) AlertPollHelper.initPoll(alert.message);
    if (!alert.recipients) alert.recipients = {};
    AlertRecipientsHelper.initRecipients(alert.recipients);
    if (!alert.sender) alert.sender = {};
    if (!alert.schedule) alert.schedule = {};
    if (!alert.location) alert.location = {};
    if (!alert.location.areas) alert.location.areas = [];

    if (config) {
      if (!alert.clientContext) alert.clientContext = {};
      alert.clientContext.templateId = config.templateId;
      alert.clientContext.isResend = isValidIntegerId(config.resendAlertId);
    }

    return alert;
  }

  static buildRequest(service: AlertModelService, defaults: AlertDefaults, trigger: string): SingleAlertDefaultsRequest {
    if (!defaults) defaults = service.alertSettings;
    const message = defaults?.message;
    const request: SingleAlertDefaultsRequest = {
      alertType: defaults?.alertType ?? AlertType.Default,
      incidentEvent: message?.incidentEvent,
      incidentId: message?.incident?.id ?? message?.incidentId,
      priority: service.useAlertPriorityV2 ? AlertPriorityHelper.getOptionalAlertPriority(message) : AlertPriorityHelper.getAlertPriority(message) ?? PriorityType.Emergency,
      relatedAlertId: service.config.relatedAlertId,
      relatedType: service.config.relatedType,
      pollQuestionId: service.config.relatedPollQuestionId,
      pollOptionId: service.config.relatedPollOptionId,
      templateId: service.config.template?.id,
      alertId: service.config.resendAlertId,
    };

    if (!trigger || trigger === AlertFields.TEMPLATE) {
      // ROAD-10504, ROAD-10580, ROAD-10583: if the trigger is for a template selection OR
      // if this is the initial fetch (no trigger), don't include priority or incident
      request.incidentEvent = undefined;
      request.incidentId = undefined;
      request.priority = undefined;
    } else {
      // ROAD-12260: else, remove alertId (resend alert ID) from config
      request.alertId = undefined;
    }
    
    if (trigger === AlertConfig.TRIGGER_VANILLA) {
      // remove templateId and other config to get "vanilla" defaults
      request.priority = undefined;
      request.relatedAlertId = undefined;
      request.pollQuestionId = undefined;
      request.pollOptionId = undefined;
      request.templateId = undefined;
      request.alertId = undefined;
    }

    return request;
  }

  // #region - Alert Priority v2 helper methods

  /**
   * Called after we fetch new alert defaults -- we use this method to merge some of the settings from defaults onto
   * our existing/working model. Depending on the `trigger` value, we will merge certain values from the new defaults
   * to our working copy (preserving any existing data, like message, recipients, etc.).
   * 
   * This method is primarily focused on enforcing rules for Alert Priority V2 (aka Alert Classification). See the 
   * other `merge*Defaults()` methods for details on what gets merged.
   * 
   * @param alert    existing/working copy of the alert we're building
   * @param defaults new/updated alert defaults from API response
   * @param trigger  the action that triggered the API request
   * @param service  the singleton alert builder service
   * @returns        `true` if we merged; else `false`
   */
  static mergeDefaults(alert: AlertDefaults, defaults: AlertDefaults, trigger: string, service?: AlertModelService): boolean {
    AlertDefaultsHelper.initDefaults(alert);
    AlertDefaultsHelper.initDefaults(defaults);

    // Apply field-related rules here as well, includes clearing data from the alert when the related field is  not
    // present. We run this against both the current _and_ incoming defaults, based on the incoming defaults `fields`.
    AlertFieldsHelper.postProcess(service, defaults, defaults.fields, trigger);
    AlertFieldsHelper.postProcess(service, alert, defaults.fields, trigger)

    switch (trigger) {
      // new incident selected - merge values that can change based on incident
      case AlertFields.INCIDENT_V1:
      case AlertFields.INCIDENT_V2: {
        this.mergeIncidentDefaults(alert, defaults, service);
        // fallthrough to PRIORITY (incident change also impacts priority)
      }
      // new priority selected - merge values that can change based on priority
      case AlertFields.PRIORITY: {
        this.mergePriorityDefaults(alert, defaults, service);
        this.mergeChannelsDefaults(alert, defaults, service);
        this.mergeSurveyDefaults(alert, defaults, service);
        return true;
      }
      // new template selected (or any other trigger) -- don't merge
      case AlertFields.TEMPLATE:
      default: {
        return false;
      }
    }
  }

  /**
   * Called by `mergeDefaults()` above when the `trigger` was selecting an incident/event (v1/v2).
   * 
   * Merges the incident-related properties: 
   *   - incident/event
   *   - mandatory groups
   *   - severity assessment
   */
  static mergeIncidentDefaults(alert: AlertDefaults, defaults: AlertDefaults, _service?: AlertModelService) {
    AlertDefaultsHelper.initDefaults(alert);
    AlertDefaultsHelper.initDefaults(defaults);

    // update: incident, mandatory groups, severity assessment, severity assessment field
    alert.message.incident = defaults.message.incident;
    alert.message.incidentId = defaults.message.incidentId;
    alert.message.incidentEvent = defaults.message.incidentEvent;
    alert.recipients.mandatoryGroupIds = defaults.recipients.mandatoryGroupIds;
    alert.severityAssessmentId = defaults.severityAssessmentId;
    alert.message.severityAssessment = defaults.message.severityAssessment;
  }

  /**
   * Called by `mergeDefaults()` above when the `trigger` was selecting a new alert priority.
   * 
   * Merges: 
   *   - the priority value on the alert MessageSettings
   */
  static mergePriorityDefaults(alert: AlertDefaults, defaults: AlertDefaults, _service?: AlertModelService) {
    AlertDefaultsHelper.initDefaults(alert);
    AlertDefaultsHelper.initDefaults(defaults);
    // update: priority
    alert.message.priority = AlertPriorityHelper.getAlertPriority(defaults.message);
  }

  /**
   * Called by `mergeDefaults()` above when the `trigger` was selecting a new alert priority.
   * 
   * We only _merge_ here (as opposed to a wholesale _replace_) so that we can preserve user data on the channels
   * (custom messages, etc).
   * 
   * Merges: 
   *   - the channel _selection_ values (send: true/false)
   *   - sub-channel _selection_ values for `textMessage`, `voice` (home, business, cell, primary, secondary)
   * 
   * Note: in addition to merging the channel selections, we also sanity-check the ETN selection to ensure that we don't
   * send to ETN when priority === NonEmergency.
   */
  static mergeChannelsDefaults(alert: AlertDefaults, defaults: AlertDefaults, service?: AlertModelService) {
    // if the feature toggle for alert priority v2 is not enabled, do not proceed
    if (!service?.useAlertPriorityV2) return;
    
    // Note: we're intentionally excluding these channels from the merging logic:
    //   eas, etn, myAlertsApp, facebook, nextdoor, twitter
    // We exclude these channels because we don't want to support having them be auto-selected in defaults based on
    // changes to the alert priority. (??? - best guess - this decision is not documented anywhere else) 
    const excludedKeys = [ 
      AlertChannelsHelper.KEY_EAS,
      AlertChannelsHelper.KEY_ETN,
      AlertChannelsHelper.KEY_MYALERTS_APP,
      AlertChannelsHelper.KEY_FACEBOOK,
      AlertChannelsHelper.KEY_TWITTER,
      AlertChannelsHelper.KEY_NEXTDOOR,
    ];

    const keys = AlertChannelsHelper.allChannelKeys().filter(k => !excludedKeys.includes(k));

    // update "send" = true/false
    keys.forEach(key => {
      const source = defaults.channels[key] as BasicChannel;
      let target = (alert.channels[key]) as BasicChannel;
      // ROAD-11629: be careful not to add channels that weren't present in defaults
      if (source?.send || target?.send) {
        if (!target) target = { send: false };
        target.send = source?.send ?? false;
      }
      alert.channels[key] = target;
    });

    // update textMessage sub-channel selections
    if (alert.channels.textMessage) {
      alert.channels.textMessage.primary = defaults.channels.textMessage?.primary ?? false;
      alert.channels.textMessage.secondary = defaults.channels.textMessage?.secondary ?? false;
    }

    // update voice sub-channel selections
    if (alert.channels.voice) {
      alert.channels.voice.cellPhone = defaults.channels.voice?.cellPhone ?? false;
      alert.channels.voice.businessPhone = defaults.channels.voice?.businessPhone ?? false;
      alert.channels.voice.homePhone = defaults.channels.voice?.homePhone ?? false;
    }

    // auto de-select etn if appropriate based on channel rules
    if (alert.channels.etn) {
      const updated = AlertChannelsHelper.autoDeselectEtnIfNonEmergency(service, alert);
      if (updated) {
        service?.snackbar?.warn('AlertClassification.EtnDeselectedForNonEmergency');
      }
    }
  }

  /**
   * Called by `mergeDefaults()` above when the `trigger` was selecting a new alert priority.
   * 
   * Merges: 
   *   - the SurveyJS survey for the sender to fill out (aka "Assessment")
   * 
   * See: ROAD-12033, ROAD-12113
   */
  static mergeSurveyDefaults(alert: AlertDefaults, defaults: AlertDefaults, _service?: AlertModelService) {
    // currently, only the sender survey is supported here
    const senderSurvey = defaults?.message?.survey?.sender;
    if (!senderSurvey || !alert) return;

    AlertSurveyHelper.applyToAlert(alert, senderSurvey, AlertSurveyHelper.FIELD_NAME_SENDER);
  }

  // #endregion - Alert Priority v2 helper methods

}